import type { ChatMessage } from "@/types";
import { type ReactNode, useEffect, useRef } from "react";
import { ChatDisclosure } from "./ChatDisclosure";
import { ChatInput } from "./ChatInput";
import { MessageBubble } from "./MessageBubble";
import { TypingIndicator } from "./TypingIndicator";

interface ChatWindowProps {
  messages: ChatMessage[];
  isTyping?: boolean;
  disabled?: boolean;
  onSend: (content: string) => void;
  /** Optional hero / welcome element rendered above the first message. */
  lead?: ReactNode;
  /** Optional slot rendered just above the input (e.g. quick-reply chips). */
  belowMessages?: ReactNode;
}

/** Scrollable message list + input. Mobile-first, fills its parent height. */
export function ChatWindow({
  messages,
  isTyping,
  disabled,
  onSend,
  lead,
  belowMessages,
}: ChatWindowProps) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, isTyping]);

  return (
    <div className="relative z-10 flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-5">
          {lead}
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              sender={m.role}
              content={m.content}
              imageUrls={m.image_urls}
            />
          ))}
          {isTyping ? <TypingIndicator /> : null}
          <div ref={endRef} />
        </div>
      </div>
      {belowMessages}
      <div className="mx-auto w-full max-w-2xl">
        <ChatDisclosure compact />
        <ChatInput disabled={disabled} onSend={onSend} />
      </div>
    </div>
  );
}
